import React from "react";
import PropTypes from "prop-types";
import { css } from "glamor";
import { Modal, Icon } from "antd";
import * as R from "ramda";

import Heading from "@arachnys/styleguide/Heading";
import { Fieldset } from "@arachnys/styleguide/Fieldset";
import { authFetch } from "../../api";

import FileUpload from "../../container/ResearchRequestForm/FileUpload";

import { BASE_URL } from "../../config";
import { parseFilenameFromContentDisposition } from "../../util";

const styles = {
    attachmentUL: css({
        padding: "0",
    }),
    attachmentItem: css({
        listStyle: "none",
        padding: "3px",
        margin: "4px 0",
        position: "relative",
        transition: "all 0.5s",
        "&:hover": {
            backgroundColor: "#e6f7ff",
        },
        "&:hover .anticon-close": {
            opacity: "1",
        },
        "& a": {
            color: "#2991FC",
            display: "inline-block",
            paddingLeft: "10px",
        },
    }),
    attachmentDelete: css({
        opacity: "0",
        transition: "opacity 0.5s",
        position: "absolute",
        right: "3px",
        top: "7px",
        cursor: "pointer",
    }),
};

class FileUploader extends React.Component {
    state = { downloadError: null };

    handleDownload = async (researchRequestUlid, id, e) => {
        e.preventDefault();
        const req = new Request(
            `${BASE_URL}/api/tickets/${researchRequestUlid}/attachments/${id}/`
        );
        try {
            await this.download(await authFetch(req));
        } catch (e) {
            this.setState({ downloadError: e });
        }
    };

    async download(response) {
        const a = document.createElement("a");
        const url = URL.createObjectURL(await response.blob());
        a.href = url;
        a.download = this.getFilename(response);
        a.click();
        URL.revokeObjectURL(url);
    }

    getFilename(response) {
        let filename = "download";
        const cd = response.headers.get("content-disposition");
        if (cd) {
            filename = parseFilenameFromContentDisposition(cd);
        }
        return filename;
    }

    async removeDocument(objectKey, researchRequestUlid, name) {
        await this.showDeleteConfirm(name);
        if (!researchRequestUlid) {
            researchRequestUlid = objectKey.split(".")[0];
        }

        const deleteUrl = `/api/tickets/${researchRequestUlid}/attachments/${objectKey}/delete/`;
        const delReq = new Request(deleteUrl, {
            method: "POST",
        });
        await authFetch(delReq);
        return true;
    }

    removeAttachedDocument = (
        objectKey,
        researchRequestUlid,
        name
    ) => async () => {
        try {
            await this.removeDocument(objectKey, researchRequestUlid, name);
            this.props.refetchFiles();
            return true;
        } catch (e) {
            return false;
        }
    };

    showDeleteConfirm(description) {
        return new Promise((res, rej) => {
            const box = Modal.confirm({
                title: "Do you want to delete this attachment",
                content: `Are you sure you want to delete "${description}"?`,
                onOk: () => {
                    box.destroy();
                    res();
                },
                onCancel: () => {
                    box.destroy();
                    rej();
                },
            });
        });
    }

    removeAntUpload = R.curry(async (researchRequest, doc) => {
        // `antd` Upload expects promise to resolve `false` to cancel
        const ANT_DELETE_CANCEL = false;
        try {
            await this.removeDocument(
                doc.response.object_key,
                researchRequest.ulid,
                doc.name
            );
            return true;
        } catch (e) {
            return ANT_DELETE_CANCEL;
        }
    });

    render() {
        if (this.state.downloadError) {
            return <div>{this.state.downloadError.message}</div>;
        }

        const { researchRequest, auth } = this.props;

        const { ulid } = researchRequest;

        const headers = {
            authorization: "Bearer " + auth.accessToken,
        };

        const submitAction = `${BASE_URL}/api/tickets/${ulid}/attachments/`;

        return (
            <div>
                <Heading level={1}>Supporting Documents</Heading>

                <Fieldset>
                    <FileUpload
                        multiple
                        headers={headers}
                        action={submitAction}
                        onRemove={this.removeAntUpload(ulid)}
                        hint=" "
                    />
                    {researchRequest && (
                        <ul {...styles.attachmentUL}>
                            {researchRequest.fileUploads.map(fp => (
                                <li key={fp.id} {...styles.attachmentItem}>
                                    <Icon type="paper-clip" />
                                    <a
                                        href="#"
                                        onClick={e =>
                                            this.handleDownload(
                                                ulid,
                                                fp.objectKey,
                                                e
                                            )
                                        }
                                    >
                                        {fp.fileName}
                                    </a>
                                    <Icon
                                        type="close"
                                        onClick={this.removeAttachedDocument(
                                            fp.objectKey,
                                            ulid,
                                            fp.fileName
                                        )}
                                        {...styles.attachmentDelete}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </Fieldset>
            </div>
        );
    }
}

FileUploader.propTypes = {
    researchRequest: PropTypes.object,
    auth: PropTypes.object,
};

export default FileUploader;
